// -----------------------------------------------------------------Imports---
import PrintIcon from "@mui/icons-material/Print";
import {
  Box,
  Button,
  Collapse,
  Container,
  Dialog,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import React, { Key, useCallback, useMemo, useState } from "react";
import { useEffect } from "react";

import Transition from "../../components/Transition/Transition";
import { getFileApiUrlBase } from "../../Global";
import { DeviceNeedItemModel, DeviceRequestModel, DeviceRequestTableKey, getDeviceRequestStatusName, tableKeys } from "../../models/DeviceRequestModel";
import ResponseModel from "../../models/ResponseModel";
import DeviceNeedItemService from "../../services/deviceNeedItems/DeviceNeedItemService";
import DeviceRequestService from "../../services/deviceRequests/DeviceRequestService";
import { getLongAddress, hasApiError } from "../../utils/Utils";

import CreateDeviceRequest from "./CreateDeviceRequest";
import { style } from "./DeviceRequests.style";

// -----------------------------------------------------------------Exports---
const DeviceRequests = (): JSX.Element => {
  const [deviceRequests, setDeviceRequests] = useState<DeviceRequestModel[]>([]);
  const [filteredDeviceRequests, setFilteredDeviceRequests] = useState<DeviceRequestModel[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState<number>(50);
  const [page, setPage] = useState<number>(0);
  const [nameFilter, setNameFilter] = useState<string>("");
  const [isSetterOpen, setSetterOpen] = useState<boolean>(false);
  const [expandedRowId, setExpandedRowId] = useState<number | null>(null);

  //get all the requests
  const getDeviceRequests = useCallback(async (): Promise<void> => {
    const response: ResponseModel = await DeviceRequestService.getDeviceRequests({});

    //showing the result
    if (hasApiError(response)) return;
    else {
      let data: DeviceRequestModel[] = response.data as DeviceRequestModel[];
      //cast some data for easier displaying
      data = data.filter((item: DeviceRequestModel): boolean => item.status !== "K" && item.status !== "X");
      data.forEach((request: DeviceRequestModel): void => {
        request.partnerName = request.deliveryAddress?.partner?.name;
        request.seat = request.deliveryAddress?.name;
        request.deviceTypeName = request.deviceType?.name;
        request.createdAtDate = new Date(request.createdAt)
          .toLocaleString()
          .substring(0, new Date(request.createdAt).toLocaleString().length - 3);
        if (request.deliveryAddress) request.fullAddress = getLongAddress(request.deliveryAddress);
      });
      setDeviceRequests(data);
      setFilteredDeviceRequests(data);
    }
  }, []);

  //download the contract and view it in a new tab
  const getContract = (request: DeviceRequestModel): void => {
    window.open(getFileApiUrlBase() + (request.deviceContract?.fileUrl ?? ""));
  };

  //call deviceNeedItem relocation for a single item
  const handleItemRelocation = async (itemId: number): Promise<void> => {
    const response: ResponseModel = await DeviceNeedItemService.relocation(itemId);
    if (hasApiError(response)) return;
    void getDeviceRequests();
  };

  //render the head once
  const headMemo: JSX.Element[] = useMemo((): JSX.Element[] => {
    return tableKeys.map((tableKey: DeviceRequestTableKey, i: Key): JSX.Element => {
      return (
        <TableCell key={i} style={style.tableHeadCell} align={tableKey.align ?? "left"}>
          {tableKey.label}
        </TableCell>
      );
    });
  }, []);

  //paging
  const handleChangePage = (_event: unknown, newPage: number): void => {
    setPage(newPage);
  };

  //changing row amount
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  //filtering the data
  const nameFilterOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setNameFilter(event.target.value);
    if (event.target.value.length > 2) {
      const newRequests: DeviceRequestModel[] = deviceRequests.filter((request: DeviceRequestModel): boolean => {
        return request.partnerName?.toLowerCase().includes(event.target.value.toLowerCase()) ?? false;
      });
      setFilteredDeviceRequests(newRequests);
    } else if (filteredDeviceRequests !== deviceRequests) {
      setFilteredDeviceRequests(deviceRequests);
    }
  };

  //get the data on load
  useEffect((): void => {
    void getDeviceRequests();
  }, [getDeviceRequests]);

  // --------------------------------------------------------------Return---
  return (
    <Paper style={style.paper}>
      {/* Header */}
      <Typography variant="h3" style={style.title}>
        Eszköz igénylések listázása
      </Typography>
      <Container style={style.container}>
        <TextField
          variant="outlined"
          value={nameFilter}
          onChange={nameFilterOnChange}
          style={style.filterTextField}
          label="Ügyfél név"
        />
        <Button onClick={(): void => setSetterOpen(true)} variant="contained" style={style.navButton}>
          Új igénylés
        </Button>
      </Container>

      {/* Table */}
      <TableContainer style={style.tableContainer}>
        <Table>
          <TableHead>
            <TableRow>
              {headMemo}
              <TableCell style={style.tableHeadCell} />
              <TableCell style={style.tableHeadCell} />
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredDeviceRequests
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((request: DeviceRequestModel, i: Key): JSX.Element => {
                const isExpanded = expandedRowId === request.id;
                return (
                  <React.Fragment key={i}>
                    <TableRow
                      style={{ ...style.dataRow, cursor: "pointer" }}
                      onClick={(): void => setExpandedRowId(isExpanded ? null : request.id)}
                    >
                      {tableKeys.map((tableKey: DeviceRequestTableKey, j: Key): JSX.Element => {
                        return (
                          <TableCell align={tableKey.align ?? "left"} style={style.dataCell} key={j}>
                            {(request as Record<string, unknown>)[tableKey.key] as string}
                          </TableCell>
                        );
                      })}
                      <TableCell align={"center"} style={style.dataCell}>
                        {request.deviceContract && request.deviceContract.fileUrl && (
                          <IconButton
                            size="large"
                            color="success"
                            onClick={(e): void => {
                              e.stopPropagation();
                              getContract(request);
                            }}
                          >
                            <PrintIcon fontSize="inherit" />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell align={"center"} style={style.dataCell}>
                        {isExpanded ? "▲" : "▼"}
                      </TableCell>
                    </TableRow>
                    <TableRow key={`${String(i)}-detail`}>
                      <TableCell style={style.accordionCell} colSpan={tableKeys.length + 2}>
                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                          <Box style={style.accordionDetails}>
                            {request.items && request.items.length > 0 ? (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell><strong>Kategória</strong></TableCell>
                                    <TableCell><strong>Eszköz típus</strong></TableCell>
                                    <TableCell><strong>Csere?</strong></TableCell>
                                    <TableCell><strong>Státusz</strong></TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {request.items.map((item: DeviceNeedItemModel, k: Key): JSX.Element => (
                                    <TableRow key={k}>
                                      <TableCell>{item.category?.name ?? item.categoryId}</TableCell>
                                      <TableCell>{item.deviceType?.name ?? item.deviceTypeId}</TableCell>
                                      <TableCell>{item.isReplacement === "I" ? "Igen" : "Nem"}</TableCell>
                                      <TableCell>{getDeviceRequestStatusName(item.status)}</TableCell>
                                      <TableCell align="center">
                                        {item.status === "E" && (
                                          <Button
                                            size="small"
                                            onClick={(): void => { void handleItemRelocation(item.id); }}
                                          >
                                            Kihelyezés
                                          </Button>
                                        )}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>
                            ) : (
                              <Typography variant="body2">Nincs tétel.</Typography>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Pagination */}
      <TablePagination
        rowsPerPageOptions={[10, 25, 50, 100, 250]}
        component="div"
        count={filteredDeviceRequests.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />

      {/* Add request modal */}
      <Dialog
        TransitionComponent={Transition}
        maxWidth={false}
        PaperProps={{ style: { width: "100%" } }}
        open={isSetterOpen}
        onClose={(): void => setSetterOpen(false)}
      >
        <CreateDeviceRequest setDialogOpen={setSetterOpen} getDeviceRequests={getDeviceRequests} />
      </Dialog>
    </Paper>
  );
};

export default DeviceRequests;
